import React, { useState } from "react";
import { Control, Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import Grid from '@material-ui/core/Grid';
import TextField from 'common/components/TextField';
import {
    CITY,
    COMPANY,
    PAYER_DATA,
    POST_CODE,
    STREET,
    STREET_NUMBER,
    TAX_ID_NUMBER,
    SUBORDINATE_INTERNAL_ID,
    IS_SUBORDINATE_UNIT,
    IS_GRV,
    HAS_NO_SUBORDINATE_INTERNAL_ID,
    HAS_NO_NIP,
} from "../../../common/constants";
import { CheckoutValues } from 'store/ducks/checkout/types';
import ErrorPanel from 'common/components/ErrorPanel';
import { makeStyles } from '@material-ui/core/styles';
import Box from "@material-ui/core/Box";
import Checkbox from "../../../../../../common/components/Checkbox";
import FormLabel from "@material-ui/core/FormLabel";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { theme } from "../../../../../../styles";

type Props = {
    checkoutValues: CheckoutValues;
    control: Control;
    errors: any;
    setValue: (name: string, value: any) => void;
    onKsefFlagsChange: (
        payerIsSubordinateUnit: boolean,
        payerIsGrv: boolean,
        payerHasNoNip: boolean,
        payerHasNoSubordinateInternalId: boolean
    ) => void;
};

const makeInputLabelClasses = makeStyles({
    shrink: {
        display: 'block',
        fontSize: '0.75rem',
        maxWidth: '90%',
        overflow: 'hidden',
        paddingLeft: '0.375rem',
        textOverflow: 'ellipsis',
        transform: 'translate(0.5rem, -0.375rem) !important',
        width: 'auto',
        whiteSpace: 'nowrap',
    },
});

const PayerData: React.FC<Props> = ({ checkoutValues, control, errors, setValue, onKsefFlagsChange }) => {
    const getName = (name: string) => `${PAYER_DATA}.${name}`;
    
    const {
        city,
        company,
        postcode,
        street,
        streetNumber,
        taxIdNumber,
        subordinateInternalId,
        isSubordinateUnit,
        isGrv,
        hasNoNip,
        hasNoSubordinateInternalId,
    } =
    checkoutValues.payerData || {};
    
    const InputLabelClasses = makeInputLabelClasses();
    
    const [ ksefInternalFields, setKsefInternalFields ] = useState({
        isSubordinateUnit: false, // jednostka podległa JST
        isGrv: false, // członek grupy VAT
        hasNoNip: false, // nie posiadam NIP
        hasNoSubordinateInternalId: false // nie posiadam identyfikatora JST
    });
    
    const [ showHasNoNip, setShowHasNoNip ] = useState(false);
    
    const handleChange = ([e]: any | EventTarget) => {
        const { name, checked } = e.target;
        const fieldName = name.split('.').pop();
        
        setKsefInternalFields((prevState) => {
            const next = { ...prevState, [fieldName]: checked }
            
            if (name === getName(IS_SUBORDINATE_UNIT) && !checked) {
                next.hasNoNip = false;
                next.hasNoSubordinateInternalId = false;
                setValue(getName(HAS_NO_NIP), false);
                setValue(getName(HAS_NO_SUBORDINATE_INTERNAL_ID), false);
            }
            
            if (name === getName(HAS_NO_NIP) && !checked) {
                next.hasNoSubordinateInternalId = false;
                setValue(getName(HAS_NO_SUBORDINATE_INTERNAL_ID), false);
            }
            
            if (name === getName(IS_GRV) && checked) {
                next.hasNoNip = false;
                next.hasNoSubordinateInternalId = false;
                setValue(getName(HAS_NO_NIP), false);
                setValue(getName(HAS_NO_SUBORDINATE_INTERNAL_ID), false);
            }
            
            if (next.hasNoNip) {
                setValue(getName(TAX_ID_NUMBER), '');
            }
            
            if (next.hasNoSubordinateInternalId) {
                setValue(getName(SUBORDINATE_INTERNAL_ID), '');
            }
            
            setShowHasNoNip(next.isSubordinateUnit && !next.isGrv);
            onKsefFlagsChange(next.isSubordinateUnit, next.isGrv, next.hasNoNip, next.hasNoSubordinateInternalId);
            
            return next;
        });
        return checked;
    }
    
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    
    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Controller
                            as={<TextField fullWidth InputLabelProps={{ classes: InputLabelClasses, shrink: true }} />}
                            control={control}
                            defaultValue={company || ''}
                            label="* Nazwa płatnika (szkoła)"
                            name={getName(COMPANY)}
                            variant="outlined"
                        />
                        <ErrorPanel errors={errors} name={getName(COMPANY)} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box alignItems="center" display="flex">
                            <Controller
                                as={<Checkbox />}
                                control={control}
                                id="payerIsSubordinateUnit"
                                name={getName(IS_SUBORDINATE_UNIT)}
                                onChange={handleChange}
                                defaultValue={isSubordinateUnit}
                                checked={ksefInternalFields.isSubordinateUnit}
                            />
                            <FormLabel htmlFor="payerIsSubordinateUnit">
                                Jednostka podległa JST
                            </FormLabel>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box alignItems="center" display="flex">
                            <Controller
                                as={<Checkbox />}
                                control={control}
                                id="payerIsGrv"
                                name={getName(IS_GRV)}
                                onChange={handleChange}
                                defaultValue={isGrv}
                                checked={ksefInternalFields.isGrv}
                            />
                            <FormLabel htmlFor="payerIsGrv">
                                Członek grupy VAT
                            </FormLabel>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start" style={{ gap: '12px', flexDirection: isMobile ? 'column' : 'row'}}>
                            <Box style={{ flexShrink: 0, width: '220px' }}>
                                <Controller
                                    as={
                                        <InputMask alwaysShowMask={false} mask="9999999999" disabled={ksefInternalFields.hasNoNip}>
                                            {(inputProps: any) => <TextField fullWidth {...inputProps} disabled={ksefInternalFields.hasNoNip}/>}
                                        </InputMask>
                                    }
                                    control={control}
                                    defaultValue={taxIdNumber || null}
                                    label="NIP"
                                    name={getName(TAX_ID_NUMBER)}
                                    variant="outlined"
                                    disabled={ksefInternalFields.hasNoNip}
                                />
                                <ErrorPanel errors={errors} name={getName(TAX_ID_NUMBER)} />
                            </Box>
                            { showHasNoNip && (
                                <Box display="flex" alignItems="center" style={{ flexShrink: 0 }}>
                                    <Controller
                                        as={<Checkbox />}
                                        control={control}
                                        id="payerHasNoNip"
                                        name={getName(HAS_NO_NIP)}
                                        onChange={handleChange}
                                        defaultValue={hasNoNip}
                                        checked={ksefInternalFields.hasNoNip}
                                    />
                                    <FormLabel
                                        htmlFor="payerHasNoNip"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        Nie posiadam numeru NIP
                                    </FormLabel>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    { ksefInternalFields.hasNoNip && (
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="flex-start" style={{ gap: "12px", flexDirection: isMobile ? 'column' : 'row' }}>
                                <Box style={{ flexShrink: 0, width: "220px" }}>
                                    <Controller
                                        as={
                                            <InputMask alwaysShowMask={false} mask="9999999999-99999" disabled={ksefInternalFields.hasNoSubordinateInternalId}>
                                                {(inputProps: any) => <TextField fullWidth {...inputProps} disabled={ksefInternalFields.hasNoSubordinateInternalId}/>}
                                            </InputMask>
                                        }
                                        control={control}
                                        defaultValue={subordinateInternalId || null}
                                        label="IDWew"
                                        name={getName(SUBORDINATE_INTERNAL_ID)}
                                        variant="outlined"
                                    />
                                    <ErrorPanel errors={errors} name={getName(SUBORDINATE_INTERNAL_ID)} />
                                </Box>
                                <Box display="flex" alignItems={isMobile ? "flex-start" : "center"} style={{ flexShrink: 0 }}>
                                    <Controller
                                        as={<Checkbox />}
                                        control={control}
                                        id="payerHasNoSubordinateInternalId"
                                        name={getName(HAS_NO_SUBORDINATE_INTERNAL_ID)}
                                        onChange={handleChange}
                                        defaultValue={hasNoSubordinateInternalId}
                                        checked={ksefInternalFields.hasNoSubordinateInternalId}
                                    />
                                    <FormLabel
                                        htmlFor="payerHasNoSubordinateInternalId"
                                        style={{ whiteSpace: isMobile ? "pre-wrap" : "nowrap" }}
                                    >
                                        Nie posiadam identyfikatora JST - chcę złożyć zamówienie bez tych danych
                                    </FormLabel>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Controller
                            as={<TextField fullWidth />}
                            control={control}
                            defaultValue={street || ''}
                            label="* Ulica"
                            name={getName(STREET)}
                            variant="outlined"
                        />
                        <ErrorPanel errors={errors} name={getName(STREET)} />
                    </Grid>
                    <Grid item xs={6} md={5}>
                        <Controller
                            as={<TextField fullWidth />}
                            control={control}
                            defaultValue={streetNumber || ''}
                            label="* Nr domu / lokalu"
                            name={getName(STREET_NUMBER)}
                            variant="outlined"
                        />
                        <ErrorPanel errors={errors} name={getName(STREET_NUMBER)} />
                    </Grid>
                    <Grid item xs={6} md={5}>
                        <Controller
                            as={
                                <InputMask alwaysShowMask={false} mask="99-999">
                                    {(inputProps: any) => <TextField {...inputProps} fullWidth />}
                                </InputMask>
                            }
                            control={control}
                            defaultValue={postcode || ''}
                            label="* Kod pocztowy"
                            name={getName(POST_CODE)}
                            variant="outlined"
                        />
                        <ErrorPanel errors={errors} name={getName(POST_CODE)} />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Controller
                            as={<TextField fullWidth />}
                            control={control}
                            defaultValue={city || ''}
                            label="* Miejscowość"
                            name={getName(CITY)}
                            variant="outlined"
                        />
                        <ErrorPanel errors={errors} name={getName(CITY)} />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default PayerData;
